/**
 * Supported settings for the cloud and local LLMs Genie actually calls.
 *
 * Live OpenRouter `supported_parameters` wins when a caller has it. When that
 * list is missing, a versioned snapshot of the public model list is used for
 * the models Genie ships, and a provider catalog is used for direct runtimes
 * (Anthropic, OpenAI, xAI, Google, DeepSeek, fal, Ollama). An unknown cloud
 * model gets no sliders — unknown support is disclosed, not invented.
 *
 * Evidence: https://openrouter.ai/api/v1/models (2026-09-29),
 * https://openrouter.ai/docs/api/reference/parameters,
 * https://docs.claude.com/en/api/messages,
 * https://docs.ollama.com/modelfile,
 * https://fal.ai/models/fal-ai/any-llm/vision.
 */
export const LLM_SETTINGS_CATALOG_VERSION = 1

export type LlmProvider = 'openrouter' | 'openai' | 'anthropic' | 'google' | 'xai' | 'deepseek' | 'fal' | 'ollama'

export interface LlmSettingControl {
  key: string
  requestField: string
  label: string
  description: string
  group: 'basic' | 'advanced'
  type: 'number' | 'integer' | 'string' | 'boolean'
  component: 'slider' | 'number' | 'select' | 'input' | 'toggle'
  min?: number
  max?: number
  step?: number
  options?: Array<{ value: string; label: string }>
}

export interface LlmSettingsExposure {
  version: number
  provider: LlmProvider | 'unknown'
  modelId: string
  source: 'discovered' | 'catalog' | 'unknown'
  controls: LlmSettingControl[]
  info: string[]
  withheld: Array<{ key: string; reason: string }>
}

export interface LlmRequestFields {
  modelId: string
  fields: Record<string, unknown>
  /** Ollama chat `options`. Empty for cloud providers. */
  options: Record<string, unknown>
  rejected: Array<{ key: string; reason: string }>
}

/** Saved seed ids that are not published OpenRouter models, mapped to the model the UI already names. */
const UNPUBLISHED_ENDPOINTS: Record<string, string> = {
  'anthropic/claude-opus-4': 'anthropic/claude-opus-4.6',
  'anthropic/claude-haiku-4': 'anthropic/claude-haiku-4.5',
  'google/gemini-3.1-pro': 'google/gemini-3.1-pro-preview',
}

/**
 * Published `supported_parameters` for models Genie ships.
 * Live discovery replaces this. Do not treat it as a generic slider list.
 */
const PUBLISHED_PARAMETERS: Record<string, { parameters: string[]; contextLength: number; maxCompletionTokens: number }> = {
  'anthropic/claude-opus-4.6': { parameters: ['include_reasoning', 'max_completion_tokens', 'max_tokens', 'reasoning', 'reasoning_effort', 'response_format', 'stop', 'structured_outputs', 'temperature', 'tool_choice', 'tools', 'top_k', 'top_p', 'verbosity'], contextLength: 1_000_000, maxCompletionTokens: 128_000 },
  'anthropic/claude-sonnet-4': { parameters: ['include_reasoning', 'max_tokens', 'reasoning', 'stop', 'temperature', 'tool_choice', 'tools', 'top_k', 'top_p'], contextLength: 200_000, maxCompletionTokens: 64_000 },
  'anthropic/claude-haiku-4.5': { parameters: ['include_reasoning', 'max_completion_tokens', 'max_tokens', 'reasoning', 'response_format', 'stop', 'structured_outputs', 'temperature', 'tool_choice', 'tools', 'top_k', 'top_p'], contextLength: 200_000, maxCompletionTokens: 64_000 },
  'openai/gpt-5.2': { parameters: ['include_reasoning', 'max_completion_tokens', 'max_tokens', 'reasoning', 'reasoning_effort', 'response_format', 'seed', 'structured_outputs', 'tool_choice', 'tools'], contextLength: 400_000, maxCompletionTokens: 128_000 },
  'openai/gpt-4o': { parameters: ['frequency_penalty', 'logit_bias', 'logprobs', 'max_completion_tokens', 'max_tokens', 'prediction', 'presence_penalty', 'response_format', 'seed', 'stop', 'structured_outputs', 'temperature', 'tool_choice', 'tools', 'top_logprobs', 'top_p', 'web_search_options'], contextLength: 128_000, maxCompletionTokens: 16_384 },
  'openai/gpt-4o-mini': { parameters: ['frequency_penalty', 'logit_bias', 'logprobs', 'max_completion_tokens', 'max_tokens', 'prediction', 'presence_penalty', 'response_format', 'seed', 'stop', 'structured_outputs', 'temperature', 'tool_choice', 'tools', 'top_logprobs', 'top_p', 'web_search_options'], contextLength: 128_000, maxCompletionTokens: 16_384 },
  'google/gemini-3.1-pro-preview': { parameters: ['include_reasoning', 'max_tokens', 'reasoning', 'reasoning_effort', 'response_format', 'seed', 'stop', 'structured_outputs', 'temperature', 'tool_choice', 'tools', 'top_p'], contextLength: 1_048_576, maxCompletionTokens: 65_536 },
  'google/gemini-2.5-flash': { parameters: ['include_reasoning', 'max_tokens', 'reasoning', 'response_format', 'seed', 'stop', 'structured_outputs', 'temperature', 'tool_choice', 'tools', 'top_p'], contextLength: 1_048_576, maxCompletionTokens: 65_535 },
  'moonshotai/kimi-k2': { parameters: ['frequency_penalty', 'max_tokens', 'presence_penalty', 'repetition_penalty', 'seed', 'stop', 'temperature', 'tool_choice', 'tools', 'top_k', 'top_p'], contextLength: 131_072, maxCompletionTokens: 98_304 },
  'minimax/minimax-m1': { parameters: ['frequency_penalty', 'include_reasoning', 'max_tokens', 'presence_penalty', 'reasoning', 'repetition_penalty', 'seed', 'stop', 'temperature', 'tool_choice', 'tools', 'top_k', 'top_p'], contextLength: 1_000_000, maxCompletionTokens: 40_000 },
  'qwen/qwen3-235b-a22b': { parameters: ['frequency_penalty', 'include_reasoning', 'max_tokens', 'presence_penalty', 'reasoning', 'response_format', 'seed', 'stop', 'temperature', 'tool_choice', 'tools', 'top_k', 'top_p'], contextLength: 131_072, maxCompletionTokens: 8_192 },
}

const EFFORT = [
  { value: 'none', label: 'None' },
  { value: 'minimal', label: 'Minimal' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
]

const WITHHELD_REASON: Record<string, string> = {
  tools: 'Tool use is not an inspector slider. This model can take tools; Genie does not send a tool list from this control.',
  tool_choice: 'Tool choice is sent with a tool list, which this control does not build.',
  response_format: 'Structured output needs a schema. It is not shown as a free-text control.',
  structured_outputs: 'Structured output needs a schema. It is not shown as a free-text control.',
  logit_bias: 'Token bias is not a readable inspector control.',
  logprobs: 'Token log probabilities are a response diagnostic, not a generation setting.',
  top_logprobs: 'Token log probabilities are a response diagnostic, not a generation setting.',
  prediction: 'Predicted output is a request optimization, not a generation setting.',
  web_search_options: 'Web search is a separate tool, not a sampling control.',
  verbosity: 'Verbosity is not a documented inspector control for this path.',
  include_reasoning: 'Reasoning effort covers whether thinking is requested. The raw include flag is not a second control.',
}

/** Direct runtimes, used only when that runtime has no per-model publication. */
const PROVIDER_PARAMETERS: Record<Exclude<LlmProvider, 'openrouter'>, string[]> = {
  openai: ['temperature', 'max_tokens', 'top_p', 'frequency_penalty', 'presence_penalty', 'seed', 'stop'],
  anthropic: ['temperature', 'max_tokens', 'top_p', 'top_k', 'stop'],
  google: ['temperature', 'max_tokens', 'top_p', 'seed', 'stop'],
  xai: ['temperature', 'max_tokens', 'top_p', 'seed', 'stop'],
  deepseek: ['temperature', 'max_tokens', 'top_p', 'frequency_penalty', 'presence_penalty', 'stop'],
  fal: ['temperature', 'max_tokens', 'reasoning'],
  ollama: ['temperature', 'num_predict', 'top_p', 'top_k', 'repeat_penalty', 'seed', 'stop', 'num_ctx'],
}

export function resolveLlmEndpoint(id: string | undefined | null): string {
  const raw = String(id ?? '').trim()
  return UNPUBLISHED_ENDPOINTS[raw] ?? raw
}

export function normalizeLlmProvider(provider: string | undefined | null): LlmProvider | 'unknown' {
  const value = String(provider ?? '').trim().toLowerCase()
  if (value === 'grok') return 'xai'
  if (value === 'local' || value === 'local-ollama') return 'ollama'
  if (value in PROVIDER_PARAMETERS || value === 'openrouter') return value as LlmProvider
  return 'unknown'
}

function controlFor(name: string, provider: LlmProvider, maxCompletionTokens: number | null): LlmSettingControl | null {
  const anthropic = provider === 'anthropic'
  const ollama = provider === 'ollama'
  const fal = provider === 'fal'
  switch (name) {
    case 'temperature':
      return {
        key: 'temperature', requestField: 'temperature', label: 'Temperature', group: 'basic', component: 'slider', type: 'number',
        min: 0, max: anthropic ? 1 : 2, step: 0.1,
        description: anthropic
          ? 'Randomness from 0 to 1. Claude rejects values above 1. Leave unset to use the provider default.'
          : 'Randomness from 0 to 2. 0 is a real setting, not “unset”. Leave unset to use the provider default.',
      }
    case 'max_tokens':
    case 'max_completion_tokens':
    case 'num_predict':
      return {
        key: 'max_tokens', requestField: ollama ? 'num_predict' : 'max_tokens', label: 'Max tokens', group: 'basic', component: 'number', type: 'integer',
        min: 1, ...(maxCompletionTokens ? { max: maxCompletionTokens } : {}), step: 1,
        description: ollama
          ? 'Maximum tokens to predict (Ollama options.num_predict). Leave unset for the runtime default.'
          : `Maximum tokens in the answer${maxCompletionTokens ? `, up to ${maxCompletionTokens.toLocaleString('en-US')}` : ''}. Leave unset for the provider default.`,
      }
    case 'reasoning':
    case 'reasoning_effort':
      if (fal) {
        return {
          key: 'reasoning', requestField: 'reasoning', label: 'Reasoning', group: 'basic', component: 'toggle', type: 'boolean',
          description: 'Ask fal any-llm to include reasoning in the answer. Off is a real setting.',
        }
      }
      return {
        key: 'reasoning_effort', requestField: 'reasoning_effort', label: 'Reasoning effort', group: 'basic', component: 'select', type: 'string', options: EFFORT,
        description: 'How much thinking to spend before the answer. Sent as reasoning_effort. Leave unset for the provider default.',
      }
    case 'top_p':
      return { key: 'top_p', requestField: 'top_p', label: 'Top P', group: 'advanced', component: 'slider', type: 'number', min: 0.01, max: 1, step: 0.01, description: 'Nucleus sampling. Lower is more focused. Leave unset for the provider default.' }
    case 'top_k':
      return { key: 'top_k', requestField: 'top_k', label: 'Top K', group: 'advanced', component: 'number', type: 'integer', min: 1, max: 500, step: 1, description: 'Only sample from the top K tokens. Leave unset for the provider default.' }
    case 'min_p':
      return { key: 'min_p', requestField: 'min_p', label: 'Min P', group: 'advanced', component: 'slider', type: 'number', min: 0, max: 1, step: 0.01, description: 'Drop tokens below this probability. 0 disables the filter.' }
    case 'top_a':
      return { key: 'top_a', requestField: 'top_a', label: 'Top A', group: 'advanced', component: 'slider', type: 'number', min: 0, max: 1, step: 0.01, description: 'Drop tokens below this fraction of the top token. 0 disables it.' }
    case 'frequency_penalty':
      return { key: 'frequency_penalty', requestField: 'frequency_penalty', label: 'Frequency penalty', group: 'advanced', component: 'slider', type: 'number', min: -2, max: 2, step: 0.1, description: 'Penalize tokens by how often they already appear, from -2 to 2.' }
    case 'presence_penalty':
      return { key: 'presence_penalty', requestField: 'presence_penalty', label: 'Presence penalty', group: 'advanced', component: 'slider', type: 'number', min: -2, max: 2, step: 0.1, description: 'Penalize tokens that have already appeared, from -2 to 2.' }
    case 'repetition_penalty':
    case 'repeat_penalty':
      return { key: 'repetition_penalty', requestField: ollama ? 'repeat_penalty' : 'repetition_penalty', label: 'Repetition penalty', group: 'advanced', component: 'slider', type: 'number', min: 0.1, max: 2, step: 0.05, description: ollama ? 'Ollama options.repeat_penalty. 1 is no extra penalty.' : 'Penalty for repeated sequences. 1 is no extra penalty.' }
    case 'seed':
      return { key: 'seed', requestField: 'seed', label: 'Seed', group: 'advanced', component: 'number', type: 'integer', min: 0, max: 2147483647, step: 1, description: 'Integer seed. 0 is a real seed, not a request to randomize. Leave unset to randomize.' }
    case 'stop':
      return { key: 'stop', requestField: anthropic ? 'stop_sequences' : 'stop', label: 'Stop sequences', group: 'advanced', component: 'input', type: 'string', description: anthropic ? 'Comma-separated sequences. Sent as stop_sequences.' : 'Comma-separated sequences that end the answer. Sent as stop.' }
    case 'num_ctx':
      return { key: 'num_ctx', requestField: 'num_ctx', label: 'Context tokens', group: 'advanced', component: 'number', type: 'integer', min: 512, max: 32768, step: 256, description: 'Ollama options.num_ctx for this request. This does not install or reload the model.' }
    default:
      return null
  }
}

function dedupe(controls: LlmSettingControl[]): LlmSettingControl[] {
  const seen = new Set<string>()
  return controls.filter((control) => {
    if (seen.has(control.key)) return false
    seen.add(control.key)
    return true
  })
}

export function exposeLlmSettings(input: {
  provider?: string | null
  modelId?: string | null
  discovered?: string[] | null
  contextLength?: number | null
  maxCompletionTokens?: number | null
}): LlmSettingsExposure {
  const provider = normalizeLlmProvider(input.provider)
  const requested = String(input.modelId ?? '').trim()
  const modelId = resolveLlmEndpoint(requested)
  const published = PUBLISHED_PARAMETERS[modelId]
  const discovered = Array.isArray(input.discovered) ? input.discovered.filter((name) => typeof name === 'string' && name) : null
  const info: string[] = []
  const withheld: Array<{ key: string; reason: string }> = []
  let names: string[] = []
  let source: LlmSettingsExposure['source'] = 'unknown'

  if (discovered && discovered.length > 0) {
    names = discovered
    source = 'discovered'
  } else if (published && (provider === 'openrouter' || provider === 'unknown')) {
    names = published.parameters
    source = 'catalog'
    info.push(`Settings from the version ${LLM_SETTINGS_CATALOG_VERSION} catalog for ${modelId}, because this model did not publish a live list.`)
    if (requested && requested !== modelId) info.push(`Saved id ${requested} is not a published model. Requests use ${modelId}.`)
  } else if (provider !== 'openrouter' && provider !== 'unknown') {
    names = PROVIDER_PARAMETERS[provider]
    source = 'catalog'
    info.push(`Settings from the ${provider} runtime catalog. This runtime does not publish a per-model list.`)
  } else {
    info.push('This model did not publish supported settings, and it is not in the versioned catalog. No sampling controls are shown.')
  }

  const maxCompletionTokens = input.maxCompletionTokens ?? published?.maxCompletionTokens ?? null
  const contextLength = input.contextLength ?? published?.contextLength ?? null
  if (contextLength) info.push(`Context: ${contextLength.toLocaleString('en-US')} tokens.`)
  if (maxCompletionTokens) info.push(`Output limit: ${maxCompletionTokens.toLocaleString('en-US')} tokens.`)

  const runtime: LlmProvider = provider === 'unknown' ? 'openrouter' : provider
  const controls = dedupe(names.flatMap((name) => {
    if (WITHHELD_REASON[name]) {
      withheld.push({ key: name, reason: WITHHELD_REASON[name] })
      return []
    }
    const control = controlFor(name, runtime, maxCompletionTokens)
    return control ? [control] : []
  }))
  const maxTokens = controls.find((control) => control.key === 'max_tokens')
  if (maxTokens && contextLength) {
    maxTokens.description += ` Context window: ${contextLength.toLocaleString('en-US')} tokens.`
  }
  if (modelId.startsWith('anthropic/')) {
    const temperature = controls.find((control) => control.key === 'temperature')
    if (temperature) {
      temperature.max = 1
      temperature.description = 'Randomness from 0 to 1. Claude rejects values above 1. Leave unset to use the provider default.'
    }
  }
  const order = ['temperature', 'max_tokens', 'reasoning_effort', 'reasoning']
  controls.sort((a, b) => (a.group === b.group ? (order.indexOf(a.key) + 1 || 9) - (order.indexOf(b.key) + 1 || 9) : a.group === 'basic' ? -1 : 1))

  return { version: LLM_SETTINGS_CATALOG_VERSION, provider, modelId, source, controls, info, withheld }
}

function reject(key: string, reason: string) {
  return { key, reason }
}

export function buildLlmRequestFields(input: {
  provider?: string | null
  modelId?: string | null
  values?: Record<string, unknown> | null
  discovered?: string[] | null
  contextLength?: number | null
  maxCompletionTokens?: number | null
}): LlmRequestFields {
  const exposure = exposeLlmSettings(input)
  const byKey = new Map(exposure.controls.map((control) => [control.key, control]))
  const fields: Record<string, unknown> = {}
  const options: Record<string, unknown> = {}
  const rejected: Array<{ key: string; reason: string }> = []
  const ollama = exposure.provider === 'ollama'
  const values = input.values ?? {}

  for (const [key, raw] of Object.entries(values)) {
    if (raw === undefined || raw === null || raw === '') continue
    const control = byKey.get(key)
    if (!control) {
      const knownElsewhere = controlFor(key, 'openai', null) || controlFor(key, 'ollama', null)
      if (knownElsewhere) rejected.push(reject(key, `${knownElsewhere.label} is not supported for ${exposure.modelId || exposure.provider}. It was not sent.`))
      continue
    }
    if (control.type === 'integer' || control.type === 'number') {
      const value = typeof raw === 'number' ? raw : Number(raw)
      if (!Number.isFinite(value)) {
        rejected.push(reject(key, `${control.label} must be a number.`))
        continue
      }
      if (control.type === 'integer' && !Number.isInteger(value)) {
        rejected.push(reject(key, `${control.label} must be a whole number.`))
        continue
      }
      if (control.min !== undefined && value < control.min) {
        rejected.push(reject(key, `${control.label} must be at least ${control.min}.`))
        continue
      }
      if (control.max !== undefined && value > control.max) {
        rejected.push(reject(key, `${control.label} must be at most ${control.max}.`))
        continue
      }
      const target = ollama ? options : fields
      target[control.requestField] = control.type === 'integer' ? value : value
      continue
    }
    if (control.type === 'boolean') {
      if (typeof raw !== 'boolean') {
        rejected.push(reject(key, `${control.label} must be on or off.`))
        continue
      }
      ;(ollama ? options : fields)[control.requestField] = raw
      continue
    }
    if (control.options && !control.options.some((option) => option.value === raw)) {
      rejected.push(reject(key, `${control.label} must be one of ${control.options.map((option) => option.label).join(', ')}.`))
      continue
    }
    const text = String(raw).trim()
    if (!text) continue
    const sent = control.key === 'stop'
      ? text.split(',').map((part) => part.trim()).filter(Boolean)
      : text
    if (control.key === 'stop' && Array.isArray(sent) && sent.length === 0) continue
    if (control.key === 'stop' && Array.isArray(sent) && sent.length > 4) {
      rejected.push(reject(key, 'Stop sequences are limited to 4.'))
      continue
    }
    ;(ollama ? options : fields)[control.requestField] = control.key === 'stop' && Array.isArray(sent) && sent.length === 1 && exposure.provider !== 'anthropic' ? sent[0] : sent
  }

  return { modelId: exposure.modelId, fields, options, rejected }
}

/** JSON Schema properties the existing settings panel already renders. */
export function llmSettingsProperties(exposure: LlmSettingsExposure): Record<string, Record<string, unknown>> {
  const properties: Record<string, Record<string, unknown>> = {}
  for (const control of exposure.controls) {
    properties[control.key] = {
      title: control.label,
      description: control.description,
      type: control.type === 'integer' ? 'integer' : control.type === 'boolean' ? 'boolean' : control.type === 'string' ? 'string' : 'number',
      group: control.group,
      omitDefault: true,
      ...(control.min !== undefined ? { minimum: control.min } : {}),
      ...(control.max !== undefined ? { maximum: control.max } : {}),
      ...(control.options ? { enum: control.options.map((option) => option.value) } : {}),
    }
  }
  return properties
}
